/* eslint-disable */
import type { CalendarMonthPaychecksAggregation } from "./CalendarMonthPaychecksAggregation.pb";
import type { Paycheck } from "./Paycheck.pb";
import type { AllocationDestination } from "./common.pb";

/**
 * A proto containing ui-affecting values that are likely to change over
 * the course of the customer's journey through the feature.
 *
 * In contrast to the UiConfiguration which contains values that are
 * likely to remain static and only change if we want to tweak copy
 * or make changes to how the Paychecks feature should look.
 */
export interface UiState {
  /**
   * The client route to be opened when the Paychecks applet is tapped.
   * This allows the backend to take care of all the logic around which
   * if any onboarding screens you should be taken to.
   *
   * Intended to be updated by setting up a DDA and by calls to
   * `SaveUserViewedDirectDepositUpsell` (squareup.cash.paychecks.api.v1)
   */
  paychecks_on_money_tab_client_route?: string;
  /**
   * Whether the user has acknowledged the "Investing Carries Risk"
   * alert. Intended to be updated by calls to
   * `SaveUserAcknowledgedRiskAlert`
   */
  customer_acknowledged_risk_alert?: boolean;
  /**
   * Used to disable the investing row in Edit Distribution if you're
   * barred from trading. Extensible for other destinations as well.
   * Only the value of the `oneof` should be considered, and none of
   * the state contained within the destination (like the
   * `investment_entity_token`)
   */
  disabled_destinations?: AllocationDestination[];
  /**
   * These are for future/predicted paychecks, not paychecks
   * in pending status (waiting to be settled)
   */
  upcoming_paychecks?: Paycheck[];
  /** Our aggregation of this month's paychecks */
  current_month_paychecks_aggregation?: UiStateCurrentMonthPaychecksAggregation;
  /** The current state of the applet. Logic determined on server-side */
  applet_state?: UiStateAppletState;
}

/**
 * The current state of the paychecks applet, determined on server-side
 * NULL_STATE is for the NUX
 * ACTIVE_STATE refers to any of the states beyond NUX
 * HIDDEN describes a state in which the paychecks feature may hidden from user
 */
export enum UiStateAppletState {
  DO_NOT_USE_CLIENT_COULD_NOT_PARSE_ENUM = "DO_NOT_USE_CLIENT_COULD_NOT_PARSE_ENUM",
  NULL_STATE = "NULL_STATE",
  ACTIVE_STATE = "ACTIVE_STATE",
  HIDDEN = "HIDDEN",
}

/**
 * The latest server-provided totals for the "current month". Current
 * month is not guaranteed to align with the user's local time. Once a
 * device's local time has rolled past the `valid_until` date, we
 * communicate to the user that they need to connect to get an accurate
 * picture of their paychecks.
 *
 * This value is used on the Money tab and Paychecks home to show
 * paychecks data.
 */
export interface UiStateCurrentMonthPaychecksAggregation {
  /** A server-computed aggregation of paychecks in the "current month" */
  calendar_month_aggregation?: CalendarMonthPaychecksAggregation;
  /**
   * A TTL date for this object, after which point the Paychecks Ui may
   * encourage the customer to connect for updated information.
   *
   * The date format is the milliseconds since the unix epoch.
   */
  valid_until?: number;
}
